import { Link, Outlet, useNavigate, useLocation } from 'react-router';
import { supabasePulse } from '../lib/supabasePulse';
import { UploadCloud, GitMerge, LogOut, Users, Building2, Bookmark, PanelLeftClose, PanelLeftOpen, DatabaseZap } from 'lucide-react';
import { PulseLogo } from './ui/PulseLogo';
import { useState } from 'react';

export default function PulseShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const handleLogout = async () => {
    await supabasePulse.auth.signOut();
    navigate('/login');
  };

  const navItems = [
    { label: 'Prospects', path: '/prospects', icon: Users },
    { label: 'Saved Lists', path: '/lists', icon: Bookmark },
    { label: 'Committees', path: '/committees', icon: Building2 },
    { label: 'Import Data', path: '/import', icon: UploadCloud },
    { label: 'Enrichment', path: '/enrichment', icon: DatabaseZap },
    { label: 'Duplicate Reconciliation', path: '/reconcile', icon: GitMerge },
  ];

  return (
    <div className="flex h-screen bg-theme-bg text-theme-text font-sans">
      {/* Sidebar */}
      <aside className={`${isSidebarCollapsed ? 'w-20' : 'w-64'} border-r border-theme-border flex flex-col bg-theme-nav transition-all duration-200`}>
        <div className={`${isSidebarCollapsed ? 'p-4' : 'p-6'} flex items-center gap-3 border-b border-theme-border/50`}>
          {!isSidebarCollapsed && <PulseLogo className="h-8 w-8 shrink-0 text-theme-cta" />}
          {!isSidebarCollapsed && <span className="font-black text-xl tracking-tight text-theme-text">PULSE</span>}
          <button
            type="button"
            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
            className="ml-auto p-2 rounded-lg text-theme-text-secondary hover:text-theme-text hover:bg-theme-nav-hover transition-colors"
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-theme-nav-active text-theme-text' 
                    : 'text-theme-text-secondary hover:bg-theme-nav-hover hover:text-theme-text'
                }`}
                title={isSidebarCollapsed ? item.label : undefined}
              >
                <Icon className="w-4 h-4" />
                {!isSidebarCollapsed && item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-theme-border/50">
          <button
            onClick={handleLogout}
            className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm font-medium text-theme-text-muted hover:bg-theme-error-bg hover:text-theme-error w-full transition-colors`}
            title={isSidebarCollapsed ? 'Sign Out' : undefined}
          >
            <LogOut className="w-4 h-4" />
            {!isSidebarCollapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        <Outlet />
      </main>
    </div>
  );
}
